import './App.css';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from 'react-router-dom';
import Home from './components/Home/Home';
import Schedule from './components/Schedule/Schedule';
import AuthGate from './components/Auth/AuthGate';
import MainLayout from './components/MainLayout/MainLayout';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import WorkWithUs from './components/WorkWithUs/WorkWithUs';
import About from './components/About/About';
import EventDetail from './components/EventDetail/EventDetail';
import PostHogPageTracker from './components/PostHogPageTracker';
import QuizList from './components/QuizBuilder/QuizList';
import QuizEditor from './components/QuizBuilder/QuizEditor';
import EventList from './components/LiveEvents/EventList';
import GameNightHub from './components/LiveEvents/GameNightHub';
import EventResults from './components/Results/EventResults';
import ScoringInterface from './components/Scoring/ScoringInterface';
import HostPanel from './components/HostPanel/HostPanel';
import PresentationView from './components/Presentation/PresentationView';
import ScoreboardView from './components/Scoreboard/ScoreboardView';
import JoinPage from './components/Join/JoinPage';
import AnswerReview from './components/AnswerReview/AnswerReview';
import OperatorShell from './components/OperatorShell/OperatorShell';
import Dashboard from './components/Dashboard/Dashboard';
import AnalyticsDashboard from './components/Analytics/AnalyticsDashboard';
import AttendanceGraph from './components/Analytics/AttendanceGraph';
import QuizPreview from './components/QuizPreview/QuizPreview';
import TriviaImport from './components/Admin/TriviaImport';
import EventsManager from './components/Admin/EventsManager';
import VenuesManager from './components/Admin/VenuesManager';
import UsersManager from './components/Admin/UsersManager';
import SiteContentManager from './components/Admin/SiteContentManager';
import BrandingSettings from './components/Admin/BrandingSettings';
import HomePageDesigner from './components/HomePage/HomePageDesigner';
import TeamsList from './components/Teams/TeamsList';
import AssociateView from './components/Teams/AssociateView';
import TeamDetail from './components/Teams/TeamDetail';
import BillingPage from './components/Billing/BillingPage';
import PlatformOrgs from './components/Platform/PlatformOrgs';
import PlatformOrgDetail from './components/Platform/PlatformOrgDetail';
import { SiteConfigProvider } from './utils/siteConfig';
import { ThemeModeProvider } from './utils/themeMode';
import BrandedTheme from './components/shared/BrandedTheme';
import SeasonsList from './components/Seasons/SeasonsList';
import SeasonDetail from './components/Seasons/SeasonDetail';
import PublicSeasonStandings, { PublicSeasonList } from './components/Seasons/PublicStandings';
import SlideLibrary from './components/Slides/SlideLibrary';
import { SignIn, SignUp, Show, RedirectToSignIn } from '@clerk/react';
import Workspace from './components/Clerk/Workspace';
import AuthLayout from './components/Auth/AuthLayout';
import { IS_OPERATOR_HOST, applyProductDocumentMeta } from './utils/productBrand';

const Page: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="page-transition">{children}</div>
);

// runmytrivia.com is the operator app, not a venue's marketing site: its
// homepage is the login/marketing split screen (the /sign-in route), not the
// public event schedule. Every other host keeps the venue homepage.
// The operator domain shows the RunMyTrivia product brand before sign-in
// and never reads the Host-header org (see utils/productBrand.ts).
applyProductDocumentMeta();

// On the operator domain the venue pages (About, Contact, event detail)
// carry West Slope Quiz content, so send them to the homepage instead.
const VenueOnly: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  IS_OPERATOR_HOST ? <Navigate to="/" replace /> : <Public>{children}</Public>;

// Clerk appearance so its <SignIn>/<SignUp> match the branded split screen
// and follow the tenant accent. Radix var()s resolve at render.
const clerkAppearance = {
  variables: {
    colorPrimary: 'var(--accent-9)',
    borderRadius: '8px',
  },
  elements: {
    card: { boxShadow: 'none', border: 'none', background: 'transparent' },
    rootBox: { width: '100%' },
    header: { display: 'none' },
    footer: { display: 'none' },
  },
} as const;

// Wrap public-facing routes (homepage, about, etc.) in MainLayout.
const Public: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <MainLayout>
    <Page>{children}</Page>
  </MainLayout>
);

// Wrap operator routes (host, score, quiz editor, dashboard, and the admin
// management screens) in one AuthGate + OperatorShell, so the whole authed
// app sits behind a single login and shares one sidebar instead of the old
// split between the operator shell and a separate tabbed /admin surface.
// The org-scoped SiteConfigProvider and BrandedTheme override the root
// (Host) ones, so the shell shows the signed-in org's name and accent.
const Operator: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AuthGate>
    <SiteConfigProvider source="org">
      <BrandedTheme>
        <OperatorShell>{children}</OperatorShell>
      </BrandedTheme>
    </SiteConfigProvider>
  </AuthGate>
);

// Padded, centered content frame for the management screens migrated out of
// the old tabbed /admin (which wrapped them in .admin-content). Dashboard,
// Quizzes, etc. manage their own layout, so only these screens need it.
const AdminScreen: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="op-admin-screen">{children}</div>
);

// Projector branding follows the org that owns the event pin, not the
// Host org, so a venue TV on any domain shows the right brand.
const PinBranding: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pin } = useParams<{ pin: string }>();
  return <SiteConfigProvider pin={pin}>{children}</SiteConfigProvider>;
};

const App: React.FC = () => {
  return (
    <SiteConfigProvider source={IS_OPERATOR_HOST ? 'product' : 'host'}>
    <ThemeModeProvider>
    <BrandedTheme>
      <ToastContainer
        limit={1}
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Router>
        <PostHogPageTracker />
        <div className="App">
          <Routes>
            {/* Public site — kept minimal: home, about, contact, the
                public event detail page that the Schedule links into. */}
            <Route
              path="/"
              element={
                IS_OPERATOR_HOST ? (
                  <>
                    <Show when="signed-in"><Navigate to="/dashboard" replace /></Show>
                    <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
                  </>
                ) : (
                  <Public><Home /><Schedule /></Public>
                )
              }
            />
            <Route path="/events/:id" element={<VenueOnly><EventDetail /></VenueOnly>} />
            <Route path="/about" element={<VenueOnly><About /></VenueOnly>} />
            <Route path="/standings" element={<Public><PublicSeasonList /></Public>} />
            <Route path="/standings/:id" element={<Public><PublicSeasonStandings /></Public>} />
            <Route path="/workwithus" element={<VenueOnly><WorkWithUs /></VenueOnly>} />

            {/* Clerk auth (operator identity, P1). Additive — the legacy JWT
                AuthGate still guards the operator routes until cutover. */}
            <Route path="/sign-in/*" element={<AuthLayout title="Sign in" subtitle="Welcome back — tonight's not going to host itself."><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" forceRedirectUrl="/workspace" appearance={clerkAppearance} /></AuthLayout>} />
            <Route path="/sign-up/*" element={<AuthLayout title="Create your account" subtitle="Set up your workspace and run your first night."><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" forceRedirectUrl="/workspace" appearance={clerkAppearance} /></AuthLayout>} />
            <Route path="/workspace" element={<><Show when="signed-in"><Workspace /></Show><Show when="signed-out"><RedirectToSignIn /></Show></>} />

            {/* Old tabbed admin surface is gone — its screens now live in
                the operator sidebar. Redirect legacy /admin links into the
                unified dashboard (AuthGate shows the login if needed). */}
            <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
            <Route path="/adminlogin" element={<Navigate to="/dashboard" replace />} />

            {/* Operator surfaces — all share AuthGate + OperatorShell */}
            <Route path="/dashboard" element={<Operator><Dashboard /></Operator>} />
            <Route path="/quizzes" element={<Operator><QuizList /></Operator>} />
            <Route path="/quizzes/:id/edit" element={<Operator><QuizEditor /></Operator>} />
            <Route path="/import" element={<Operator><AdminScreen><TriviaImport /></AdminScreen></Operator>} />
            <Route path="/live-events" element={<Operator><EventList /></Operator>} />
            <Route path="/schedule" element={<Operator><AdminScreen><EventsManager /></AdminScreen></Operator>} />
            <Route path="/analytics" element={<Operator><AnalyticsDashboard /></Operator>} />
            <Route path="/attendance" element={<Operator><AdminScreen><AttendanceGraph /></AdminScreen></Operator>} />
            <Route path="/teams" element={<Operator><AdminScreen><TeamsList /></AdminScreen></Operator>} />
            <Route path="/teams/associate" element={<Operator><AdminScreen><AssociateView /></AdminScreen></Operator>} />
            <Route path="/teams/:id" element={<Operator><AdminScreen><TeamDetail /></AdminScreen></Operator>} />
            <Route path="/seasons" element={<Operator><AdminScreen><SeasonsList /></AdminScreen></Operator>} />
            <Route path="/seasons/:id" element={<Operator><AdminScreen><SeasonDetail /></AdminScreen></Operator>} />
            <Route path="/slides" element={<Operator><AdminScreen><SlideLibrary /></AdminScreen></Operator>} />
            <Route path="/venues" element={<Operator><AdminScreen><VenuesManager /></AdminScreen></Operator>} />
            <Route path="/home-page" element={<Operator><AdminScreen><HomePageDesigner /></AdminScreen></Operator>} />
            <Route path="/site-content" element={<Operator><AdminScreen><SiteContentManager /></AdminScreen></Operator>} />
            <Route path="/users" element={<Operator><AdminScreen><UsersManager /></AdminScreen></Operator>} />
            <Route path="/billing" element={<Operator><AdminScreen><BillingPage /></AdminScreen></Operator>} />
            <Route path="/platform" element={<Operator><AdminScreen><PlatformOrgs /></AdminScreen></Operator>} />
            <Route path="/platform/orgs/:id" element={<Operator><AdminScreen><PlatformOrgDetail /></AdminScreen></Operator>} />
            <Route path="/branding" element={<Operator><AdminScreen><BrandingSettings /></AdminScreen></Operator>} />
            <Route path="/events/:id/hub" element={<Operator><GameNightHub /></Operator>} />
            <Route path="/events/:id/score" element={<Operator><ScoringInterface /></Operator>} />
            <Route path="/events/:id/host" element={<Operator><HostPanel /></Operator>} />
            <Route path="/events/:id/results" element={<Operator><EventResults /></Operator>} />
            <Route path="/events/:id/answers" element={<Operator><AnswerReview /></Operator>} />

            {/* Audience screen — no chrome at all */}
            <Route path="/present/:pin" element={<PinBranding><PresentationView /></PinBranding>} />

            {/* TV scoreboard — always-on standings for a second screen. */}
            <Route path="/scoreboard/:pin" element={<ScoreboardView />} />

            {/* Team phone check-in from the projector QR code. No account,
                no chrome. */}
            <Route path="/join" element={<JoinPage />} />
            <Route path="/join/:pin" element={<JoinPage />} />

            {/* Quiz preview — also no chrome, drives slides from quiz
                content directly without a live event. */}
            <Route path="/quizzes/:id/preview" element={<SiteConfigProvider source="org"><QuizPreview /></SiteConfigProvider>} />
          </Routes>
        </div>
      </Router>
    </BrandedTheme>
    </ThemeModeProvider>
    </SiteConfigProvider>
  );
};

export default App;
